import React, { memo, useContext, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import { set } from 'lodash';
import type { IGooglePublisherTagIntegration } from 'src/types';
import { clientLog } from 'common/utils/helpers';
import GlobalStyles from 'common/components/GlobalStyles';
import { isAndroid, isIOS } from 'common/utils/environment';
import { useCountDown } from 'common/components/useCountDown';
import { useWindowSize } from 'common/components/useWindowSize';
import {
	DEFAULT_MEDIA_QUERY_PLATFORMS,
	GPT_AD_DEFAULT_CLOSING_FN,
	GPT_AD_DEFAULT_CONTAINER_ID,
} from 'common/constants';
import { TimerEvEm } from 'client/components/common/BuildingBlocks/Timer/utils';
import { Context as ResponsiveProviderContext } from 'client/components/common/ResponsiveProvider/Context';
import css from './GooglePublisherTagTracker.scss';

const log = clientLog.extend('gpt-tracker');
const { MOBILE, MOBILE_LANDSCAPE } = DEFAULT_MEDIA_QUERY_PLATFORMS;
const countdownFPS = 1000 / 60;
const skipBtnText = { he: 'דלג', en: 'Skip' };
let cardIndex = 0;
const getProgress = (timeLeft: number, autoCloseInterval: number): number => {
	const progress = timeLeft / autoCloseInterval;
	return Number.isNaN(progress) ? 0 : progress;
};

type Props = {
	settings: IGooglePublisherTagIntegration;
	cardId: string;
	lang: string;
};

/**
 * {@link https://developers.google.com/publisher-tag/reference googletag reference}
 * {@link https://developers.google.com/publisher-tag/samples/ad-event-listeners googletag listeners}
 *
 * @example mako
 * large = { path: '/242748299/mako/hitlist/PRIME', size: [970, 330] };
 * small = { path: '/242748299/mako/hitlist/maavaron', size: [320, 410] };
 *
 * @example google
 * large = { path: '/6355419/Travel/Europe', size: [750, 200] };
 * small = { path: '/6355419/Travel/Europe', size: [300, 250] };
 *
 * @description
 * To debug it in development environment, go to `preview.ejs` and uncomment <!-- DFP(GPT) Banner -->
 *
 * @todo how do detect `ad close` event, when closed by internal ad script
 */
const GooglePublisherTagTracker: React.FC<Props> = ({ cardId, settings, lang }) => {
	const [isClosed, setIsClosed] = React.useState(false);
	const [isActive, setIsActive] = React.useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { currentMediaQuery } = useContext(ResponsiveProviderContext);
	const { showInitially, showBetweenCards, timeout: autoCloseInterval = 0 } = settings;
	const {
		pause: countdownPause,
		start: countdownStart,
		...countdown
	} = useCountDown(autoCloseInterval * 1000, countdownFPS);
	const winSize = useWindowSize({ debounce: 300 });

	const containerId = settings.containerId || GPT_AD_DEFAULT_CONTAINER_ID;
	const isMobile = currentMediaQuery === MOBILE || currentMediaQuery === MOBILE_LANDSCAPE;
	const desktopSettings = settings.large?.path ? settings.large : undefined;
	const iosSettings = settings.ios?.path ? settings.ios : undefined;
	const androidSettings = settings.android?.path ? settings.android : undefined;
	const mobileSettings = settings.small?.path
		? settings.small
		: androidSettings && isAndroid()
			? androidSettings
			: iosSettings && isIOS()
				? iosSettings
				: desktopSettings;
	const { size, path, targets, closeFn } = (isMobile ? mobileSettings : desktopSettings) ?? {};
	const apiReady = window.googletag && googletag.apiReady;
	const fitToScreen = isMobile;

	const onClose = useCallback(
		(_isClosed: boolean = true) => {
			setIsClosed(_isClosed);
			setIsActive(false);
			// Stop auto-close countdown
			countdownPause();
			// Start card timer
			TimerEvEm.emit('start', { cardId });
		},
		[countdownPause, cardId]
	);

	useEffect(() => {
		if (countdown.isComplete) {
			onClose();
		} else {
			// Pause card timer (emit it on every `timeLeft` change, because timer start time is unknown)
			TimerEvEm.emit('pause', { cardId });
		}
	}, [countdown.isComplete, onClose, countdown.timeLeft, cardId]);

	useEffect(() => {
		log('mount');
		return () => {
			if (typeof window.googletag?.destroySlots === 'function') {
				window.googletag.destroySlots();
			}
			cardIndex += 1;
			log('unmount');
		};
	}, []);

	useEffect(
		function enableAdEffect() {
			log('enableAdEffect', cardIndex);
			const { googletag } = window;

			if (
				!apiReady ||
				!path ||
				!size ||
				(!showInitially && cardIndex === 0) ||
				(!showBetweenCards && cardIndex > 0)
			) {
				return;
			}

			(window as any).DFP = {
				// This listener handle ad close in 'mako' ads (desktop platform)
				getDecision: function getDecision(_height: number, _width: number, _isClosed: boolean, _divId: string) {
					log('getDecision');
					onClose(_isClosed);
				},
				// This listener handle ad close in 'mako' ads (mobile platform)
				onMaavaronClose: function onMaavaronClose() {
					log('onMaavaronClose');
					onClose();
				},
			};

			// set the close function to access close adv from the mounted iframe
			set(window, closeFn || GPT_AD_DEFAULT_CLOSING_FN, onClose);

			googletag.cmd.push(function enableAd() {
				const slot = googletag.defineSlot(path, size, containerId);
				log('enableAd');
				if (!slot) {
					return;
				}

				// Start auto-close countdown
				countdownStart();

				slot.addService(googletag.pubads());

				/**
				 * Configure slot-level targeting.
				 * {@link https://developers.google.com/publisher-tag/guides/key-value-targeting#set_targeting source}
				 */
				if (Array.isArray(targets)) {
					targets.forEach((target: Partial<string[]>) => {
						const key = target[0];
						const value = target[1]?.split(',').map(t => t.trim());
						if (key && value) slot.setTargeting(key, value);
					});
				}

				// This listener will be called when a slot has finished rendering.
				googletag.pubads().addEventListener('slotRenderEnded', event => {
					log('slotRenderEnded', event);
					setIsActive(!event.isEmpty);
				});

				// Enable SRA and services.
				googletag.pubads().enableSingleRequest();
				googletag.enableServices();
				googletag.display(slot);
			});
		},
		[
			apiReady,
			showInitially,
			showBetweenCards,
			size,
			path,
			targets,
			onClose,
			countdownStart,
			cardId,
			containerId,
			closeFn,
		]
	);

	const [width, height] = size ?? [];

	if (!width || !height || !path) {
		return null;
	}

	const scale = fitToScreen ? Math.min(winSize.innerWidth / width, winSize.innerHeight / height) : 1;
	const stretchWidth = isMobile && (settings.stretch ?? true);
	const stretchHeight = isMobile && (settings.stretchHeight ?? true);

	return isClosed ? null : (
		<div
			className={cn(css.ad, {
				[css.active]: isActive,
				[css.mobile]: isMobile,
				[css.fit]: fitToScreen,
				[css.stretchX]: stretchWidth,
				[css.stretchY]: stretchHeight,
			})}
		>
			<GlobalStyles>
				{`.${css.adContainer} {
					width: ${stretchWidth ? winSize.innerWidth : width * scale}px !important;
					height: ${stretchHeight ? winSize.innerHeight : height * scale}px !important;
				}`}
			</GlobalStyles>
			<div className={css.adInner}>
				<div className={css.adContainer} id={containerId} ref={containerRef} />
				<button type="button" className={css.adClose} onClick={() => onClose()}>
					{skipBtnText[lang.toLowerCase()]}
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
						<circle cx="16" cy="16" r="16" />
						<circle
							cx="16"
							cy="16"
							r="16"
							style={{
								strokeDashoffset:
									(101 - (1 - getProgress(countdown.timeLeft, autoCloseInterval)) * 101) / 1000,
								strokeDasharray: '101 101',
								transition: 'stroke-dashoffset 10ms linear',
							}}
						/>
					</svg>
				</button>
			</div>
		</div>
	);
};

export default memo(GooglePublisherTagTracker);
